import dayjs, { Dayjs } from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

export type AggregationLevel = 'day' | 'week' | 'month';

export const getDefaultAggregation = (startDate: Dayjs, endDate: Dayjs): AggregationLevel => {
  const daysDiff = endDate.diff(startDate, 'day');
  if (daysDiff <= 60) return 'day';     // Up to 2 months: daily
  if (daysDiff <= 365) return 'week';   // Up to 1 year: weekly  
  return 'month';                       // Over 1 year: monthly
};

export const getAvailableAggregations = (startDate: Dayjs, endDate: Dayjs): AggregationLevel[] => {
  // Always return all three options regardless of date range
  return ['day', 'week', 'month'];
};

export interface DateAggregatedData {
  date: string;
  label: string;
  count: number;
}

// Generate complete date range to ensure no gaps in data
const generateDateRange = (startDate: Dayjs, endDate: Dayjs, level: AggregationLevel): string[] => {
  const dates: string[] = [];
  let current = startDate;
  
  // Optimize for large date ranges by limiting iterations
  const maxIterations = level === 'day' ? 1100 : level === 'week' ? 260 : 60; // ~3 years daily, ~5 years weekly/monthly
  let iterations = 0;
  
  while ((current.isBefore(endDate) || current.isSame(endDate)) && iterations < maxIterations) {
    if (level === 'day') {
      dates.push(current.format('YYYY-MM-DD'));
      current = current.add(1, 'day');
    } else if (level === 'week') {
      const isoWeekDate = current.startOf('isoWeek');
      const year = isoWeekDate.year();
      const week = current.isoWeek();
      dates.push(`${year}-W${week.toString().padStart(2, '0')}`);
      current = current.add(1, 'week');
    } else {
      dates.push(current.format('YYYY-MM'));
      current = current.add(1, 'month');
    }
    iterations++;
  }
  
  if (iterations >= maxIterations) {
    console.warn(`Date range too large for ${level} aggregation. Limited to ${maxIterations} data points.`);
  }
  
  return Array.from(new Set(dates)); // Remove duplicates
};

export const aggregateDataByDate = (
  data: Array<{ date: string; count: number }>,
  level: AggregationLevel,
  startDate: Dayjs,
  endDate: Dayjs
): DateAggregatedData[] => {
  // Early return for empty data
  if (!data.length) {
    const range = generateDateRange(startDate, endDate, level);
    return range.map(key => ({
      date: key,
      label: key.includes('-W') ? 
        dayjs().year(parseInt(key.split('-W')[0])).isoWeek(parseInt(key.split('-W')[1])).startOf('isoWeek').format('YYYY-MM-DD') : 
        key,
      count: 0
    }));
  }
  
  // Performance check for very large date ranges
  const daysDiff = endDate.diff(startDate, 'day');
  if (daysDiff > 1095 && level === 'day') { // More than 3 years with daily aggregation
    console.warn(`Large date range detected (${daysDiff} days) with daily aggregation. Consider using weekly or monthly aggregation for better performance.`);
  }
  
  // Group data in a single pass
  const grouped = new Map<string, number>();
  
  for (const item of data) {
    const date = dayjs(item.date);
    let key: string;
    
    if (level === 'day') {
      key = date.format('YYYY-MM-DD');
    } else if (level === 'week') {
      // Use ISO week format to handle year boundaries correctly
      const isoWeekDate = date.startOf('isoWeek');
      const year = isoWeekDate.year();
      const week = date.isoWeek();
      key = `${year}-W${week.toString().padStart(2, '0')}`;
    } else {
      key = date.format('YYYY-MM');
    }
    
    grouped.set(key, (grouped.get(key) || 0) + item.count);
  }
  
  // Generate complete date range to ensure no gaps
  const completeDateRange = generateDateRange(startDate, endDate, level);
  
  // Build result array with optimized label generation
  const result: DateAggregatedData[] = [];
  
  for (const key of completeDateRange) {
    let label = key;
    if (key.includes('-W')) {
      // Format week labels as the first day (Monday) of the ISO week
      const [year, week] = key.split('-W');
      const firstDayOfWeek = dayjs().year(parseInt(year)).isoWeek(parseInt(week)).startOf('isoWeek');
      label = firstDayOfWeek.format('YYYY-MM-DD');
    }
    
    result.push({
      date: key,
      label,
      count: grouped.get(key) || 0
    });
  }
  
  return result;
};